import { axios } from "./client"
import type {
  CameraExid,
  CopilotConversation,
  CopilotMessage,
  CopilotMessageStep,
  CountsParams,
  FeedbackPayload,
  GrafanaKitMetrics,
  LabsPaginationParams,
  LuminanceReading,
  MilesightCameraNetworkConfiguration,
  MilesightCameraOsdConfiguration,
  MilesightCameraSdCardInfo,
  MilesightCameraSystemSettings,
  MilesightCameraVideoConfig,
  MilesightRequestParams,
  PaginatedItems,
  SegmentsPresenceByLabel,
} from "@evercam/api/types"
import { GrafanaMetricId, KitMetricPeriod } from "@evercam/api/types"

export function getLabsBaseUrl() {
  const labsPrNumber = /labs-(\d+)/g.exec(window.location.href)?.[1]

  return labsPrNumber
    ? `https://${labsPrNumber}.labs.evercam.io`
    : axios.env.evercamLabsUrl
}

export const EvercamLabsApi = {
  milesight: {
    getVideoConfig(
      { host, httpPort, username, password }: MilesightRequestParams,
      config = {}
    ): Promise<MilesightCameraVideoConfig> {
      return axios.get(`${getLabsBaseUrl()}/milesight/video`, {
        params: {
          host,
          port: httpPort,
          username,
          password,
        },
        ...config,
      })
    },
    getNetworkConfiguration(
      { host, httpPort, username, password }: MilesightRequestParams,
      config = {}
    ): Promise<MilesightCameraNetworkConfiguration> {
      return axios.get(`${getLabsBaseUrl()}/milesight/network`, {
        params: {
          host,
          port: httpPort,
          username,
          password,
        },
        ...config,
      })
    },
    getSystemSettings(
      { host, httpPort, username, password }: MilesightRequestParams,
      config = {}
    ): Promise<MilesightCameraSystemSettings> {
      return axios.get(`${getLabsBaseUrl()}/milesight/system`, {
        params: {
          host,
          port: httpPort,
          username,
          password,
        },
        ...config,
      })
    },
    getTimeSettings(
      { host, httpPort, username, password }: MilesightRequestParams,
      config = {}
    ): Promise<MilesightCameraSystemSettings> {
      return axios.get(`${getLabsBaseUrl()}/milesight/time`, {
        params: {
          host,
          port: httpPort,
          username,
          password,
        },
        ...config,
      })
    },
    getOsdConfiguration(
      { host, httpPort, username, password }: MilesightRequestParams,
      config = {}
    ): Promise<MilesightCameraOsdConfiguration> {
      return axios.get(`${getLabsBaseUrl()}/milesight/osd`, {
        params: {
          host,
          port: httpPort,
          username,
          password,
        },
        ...config,
      })
    },
    getSdCardInfo(
      { host, httpPort, username, password }: MilesightRequestParams,
      config = {}
    ): Promise<MilesightCameraSdCardInfo> {
      return axios.get(`${getLabsBaseUrl()}/milesight/storage`, {
        params: {
          host,
          port: httpPort,
          username,
          password,
        },
        ...config,
      })
    },
  },
  posthog: {
    getEvents(params: any): Promise<PaginatedItems<any>> {
      return axios.get(`${getLabsBaseUrl()}/posthog/events`, {
        params,
      })
    },
    getUsers(params: any): Promise<any> {
      return axios.get(`${getLabsBaseUrl()}/posthog/users`, {
        params,
      })
    },
  },
  copilot: {
    async getAllConversations(
      params: LabsPaginationParams & {
        cameraExid?: string
        projectExid?: string
        user?: string
      }
    ): Promise<PaginatedItems<CopilotConversation>> {
      return axios.get(`${getLabsBaseUrl()}/copilot/conversations`, {
        params,
        preserveCasing: true,
      })
    },
    async getOneConversation(
      conversationId: number
    ): Promise<PaginatedItems<CopilotConversation>> {
      return axios.get(
        `${getLabsBaseUrl()}/copilot/conversations/${conversationId}`,
        {
          preserveCasing: true,
        }
      )
    },
    async getConversationMessages(
      conversationId: number,
      params: LabsPaginationParams
    ): Promise<PaginatedItems<CopilotMessage>> {
      return axios.get(
        `${getLabsBaseUrl()}/copilot/conversations/${conversationId}/messages`,
        { params, preserveCasing: true }
      )
    },
    async getMessageSteps(
      message: CopilotMessage
    ): Promise<CopilotMessageStep[]> {
      return axios.get(
        `${getLabsBaseUrl()}/copilot/conversations/${
          message.conversationId
        }/messages/${message.id}/steps`
      )
    },
    async submitMessageFeedback<T>({
      conversationId,
      messageId,
      ...params
    }: FeedbackPayload<T>) {
      return axios.post(
        `${getLabsBaseUrl()}/copilot/conversations/${conversationId}/messages/${messageId}/feedback`,
        params,
        {
          preserveCasing: true,
        }
      )
    },
  },
  detections: {
    async getSegmentsDateIntervals({
      cameraExid,
      fromDate,
      toDate,
      precision,
    }: CountsParams): Promise<SegmentsPresenceByLabel> {
      return axios.get(
        `${getLabsBaseUrl()}/cameras/${cameraExid}/segments/presence-intervals`,
        {
          params: { fromDate, toDate, precision },
          preserveCasing: true,
        }
      )
    },
  },
  googleCloudStorage: {
    listFiles(
      prefix: string = ""
    ): Promise<{ files: string[]; folders: string[] }> {
      return axios.get(
        `${getLabsBaseUrl()}/gc-storage/buckets/evercam-labs/files`,
        {
          params: {
            prefix,
          },
          preserveCasing: true,
        }
      )
    },
    getSignedUrl(fileName: string): Promise<{ url: string }> {
      return axios.get(
        `${getLabsBaseUrl()}/gc-storage/buckets/evercam-labs/files/signed-url`,
        {
          params: {
            fileName,
          },
          preserveCasing: true,
        }
      )
    },
  },
  luminance: {
    getReadings(params: {
      cameraExid: CameraExid
      fromDate: string
      toDate: string
    }): Promise<LuminanceReading[]> {
      return axios.get(
        `${getLabsBaseUrl()}/cameras/${
          params.cameraExid || "echel-mdqur"
        }/luminance/readings`,
        {
          params: {
            fromDate: params.fromDate,
            toDate: params.toDate,
          },
          preserveCasing: true,
        }
      )
    },
  },
  kits: {
    getMetrics(
      kitId: string,
      metrics: GrafanaMetricId[],
      period: KitMetricPeriod = KitMetricPeriod.Last1Hour
    ): Promise<GrafanaKitMetrics> {
      return axios.get(`${getLabsBaseUrl()}/kits/${kitId}/metrics`, {
        params: {
          metrics,
          from: period,
        },
        preserveCasing: true,
      })
    },
  },
}
